import React, { memo, MouseEvent, useCallback, useRef } from 'react'
import Modal from '@components/Modal/Modal'
import useKeypress from '@hooks/useKeyPress'
import { useTranslation } from 'react-i18next'

import closeIcon from '@assets/images/nav/close.png'
import closeHoverIcon from '@assets/images/nav/close-hover.png'
import './ModalDialog.scss'
import Button from '@components/Button/Button'
import { useOutsideClick } from '@hooks/useOutsideClick'
import { NavigateFunction } from 'react-router'

interface OwnProps {
  extraClass?: string
  title?: string
  prefixTitleIcon?: string
  suffixTitleIcon?: string
  bodyText?: string
  bodyComponent?: React.ReactNode
  footerComponent?: React.ReactNode
  visible: boolean
  actionText?: string
  action?: () => void
  onClose: (e?: MouseEvent<HTMLButtonElement> | MouseEvent) => void
}

export const handleInfoUrl = (url: string | undefined, navigateHandler: NavigateFunction) => {
  if (!url) {
    return
  }

  if (url.startsWith('http://') || url.startsWith('https://')) {
    window.open(url, '_blank')
  } else {
    navigateHandler(url)
  }
}

export const handleImageMouseEnterOrLeave = (e: MouseEvent, image: string) => {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  //@ts-ignore
  e.target.src = image
}

export const ModalDialog = ({
  title,
  prefixTitleIcon,
  suffixTitleIcon,
  bodyText,
  bodyComponent,
  footerComponent,
  visible,
  actionText,
  action,
  onClose,
  extraClass,
}: OwnProps) => {
  const { t } = useTranslation('MainMenu')
  useKeypress('Escape', onClose)
  const modalRef = useRef<HTMLDivElement>(null)
  useOutsideClick(modalRef, onClose)

  const handleHeaderClose = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation()
      e.preventDefault()
      onClose(e)
    },
    [onClose],
  )

  if (!visible) {
    return null
  }

  return (
    <Modal extraClass="main Modal-blur">
      <div className={`ModalDialog ${extraClass}`} ref={modalRef}>
        <header className="ModalDialog-header">
          {prefixTitleIcon && <img className="ModalDialog-header-icon" src={prefixTitleIcon} />}
          {title && <span className="ModalDialog-header-title">{title}</span>}
          {suffixTitleIcon && <img className="ModalDialog-header-icon" src={suffixTitleIcon} />}

          <button onClick={handleHeaderClose}>
            <img
              src={closeIcon}
              alt={t('close')}
              onMouseOver={(e) => handleImageMouseEnterOrLeave(e, closeHoverIcon)}
              onMouseLeave={(e) => handleImageMouseEnterOrLeave(e, closeIcon)}
            />
          </button>
        </header>

        <div className="ModalDialog-body">
          {bodyComponent}
          <p className="ModalDialog-body-text">{bodyText}</p>
        </div>

        {footerComponent && (
          <footer className="ModalDialog-footer">
            {footerComponent}
            {action && <Button onClick={action}>{actionText ?? t('ok', { ns: 'Buttons' })}</Button>}
          </footer>
        )}
      </div>
    </Modal>
  )
}

export default memo(ModalDialog)
